import { Component, signal, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from './api.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, CommonModule, RouterModule],
  template: `
    <!-- Template remains identical -->
    <div class="min-h-screen flex font-sans" style="background:#f1f9fe;">
      <div class="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col items-center justify-center p-12" style="background:linear-gradient(145deg, #0C3A5A 0%, #1A6FA6 50%, #2B9AD2 100%);">
        <div class="absolute -top-20 -left-20 w-72 h-72 rounded-full opacity-10" style="background:#5EC9F0;"></div>
        <div class="absolute -bottom-32 -right-16 w-96 h-96 rounded-full opacity-10" style="background:#F6AF26;"></div>
        <div class="absolute top-1/3 right-8 w-32 h-32 rounded-full opacity-10" style="background:#ffffff;"></div>
        <div class="relative z-10 text-center">
          <div class="flex items-center justify-center mb-8">
            <img src="/logo-dark.png" alt="NZ Plumbing" class="h-16 w-auto" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="hidden items-center gap-4">
              <div class="w-14 h-14 rounded-3xl flex items-center justify-center" style="background:rgba(255,255,255,0.15);"><svg viewBox="0 0 24 24" class="w-8 h-8 fill-white" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C12 2 5 8.5 5 14a7 7 0 0014 0c0-5.5-7-12-7-12z"/></svg></div>
              <div><p class="text-white font-bold text-2xl leading-tight">NZ Plumbing</p><p class="text-white/60 text-sm tracking-widest uppercase">Limited</p></div>
            </div>
          </div>
          <h2 class="text-white text-3xl font-bold mb-4 leading-snug">Auckland's Plumbing<br>Operations Platform</h2>
          <p class="text-white/70 text-base leading-relaxed max-w-sm mx-auto">Manage your fleet, dispatch jobs, handle compliance and invoicing — all from one modern admin portal.</p>
          <div class="mt-10 flex flex-wrap justify-center gap-3">
            <span class="px-4 py-2 rounded-full text-sm font-semibold text-white" style="background:rgba(255,255,255,0.12);">✓ Live Fleet Tracking</span>
            <span class="px-4 py-2 rounded-full text-sm font-semibold text-white" style="background:rgba(255,255,255,0.12);">✓ Instant Dispatch</span>
            <span class="px-4 py-2 rounded-full text-sm font-semibold text-white" style="background:rgba(255,255,255,0.12);">✓ AI Insurance Assist</span>
          </div>

          <!-- Licences Section -->
          <div class="mt-12 w-full max-w-sm mx-auto">
            <div class="flex items-center justify-between mb-5">
              <h3 class="text-white font-bold text-base tracking-wide">My 2026–27 Licences</h3>
              <span class="text-white/50 text-xs font-semibold uppercase tracking-widest">Plumbers NZ</span>
            </div>
            <div class="grid grid-cols-3 gap-3">
              <!-- Certifying Plumber -->
              <div class="rounded-2xl overflow-hidden shadow-lg flex flex-col" style="background:linear-gradient(160deg,#7C3AED,#5B21B6);">
                <div class="flex items-center justify-center pt-5 pb-3">
                  <div class="w-12 h-12 rounded-full border-4 border-white/30 flex items-center justify-center" style="background:rgba(255,255,255,0.1);">
                    <svg viewBox="0 0 24 24" class="w-6 h-6 fill-white" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="4"/><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 16a6 6 0 1 1 6-6 6 6 0 0 1-6 6z"/></svg>
                  </div>
                </div>
                <div class="px-3 pb-1 text-center">
                  <p class="text-white/60 text-[9px] font-bold uppercase tracking-widest leading-tight">Certifying</p>
                  <p class="text-white font-extrabold text-xs leading-tight mt-0.5">Plumber</p>
                </div>
                <div class="mx-3 mt-2 mb-3 py-1 rounded-lg text-center" style="background:rgba(255,255,255,0.15);">
                  <p class="text-white font-bold text-[10px]">Renewed</p>
                </div>
                <button class="mx-3 mb-3 py-1.5 rounded-lg text-center transition-all hover:opacity-90" style="background:rgba(255,255,255,0.2); color:#E9D5FF; font-size:10px; font-weight:700;">View Licence</button>
              </div>

              <!-- Certifying Gasfitter -->
              <div class="rounded-2xl overflow-hidden shadow-lg flex flex-col" style="background:linear-gradient(160deg,#D97706,#92400E);">
                <div class="flex items-center justify-center pt-5 pb-3">
                  <div class="w-12 h-12 rounded-full border-4 border-white/30 flex items-center justify-center" style="background:rgba(255,255,255,0.1);">
                    <svg viewBox="0 0 24 24" class="w-6 h-6 fill-white" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C12 2 5 8.5 5 14a7 7 0 0014 0c0-5.5-7-12-7-12z"/></svg>
                  </div>
                </div>
                <div class="px-3 pb-1 text-center">
                  <p class="text-white/60 text-[9px] font-bold uppercase tracking-widest leading-tight">Certifying</p>
                  <p class="text-white font-extrabold text-xs leading-tight mt-0.5">Gasfitter</p>
                </div>
                <div class="mx-3 mt-2 mb-3 py-1 rounded-lg text-center" style="background:rgba(255,255,255,0.15);">
                  <p class="text-white font-bold text-[10px]">Renewed</p>
                </div>
                <button class="mx-3 mb-3 py-1.5 rounded-lg text-center transition-all hover:opacity-90" style="background:rgba(255,255,255,0.2); color:#FDE68A; font-size:10px; font-weight:700;">View Licence</button>
              </div>

              <!-- Certifying Drainlayer -->
              <div class="rounded-2xl overflow-hidden shadow-lg flex flex-col" style="background:linear-gradient(160deg,#0D9488,#065F46);">
                <div class="flex items-center justify-center pt-5 pb-3">
                  <div class="w-12 h-12 rounded-full border-4 border-white/30 flex items-center justify-center" style="background:rgba(255,255,255,0.1);">
                    <svg viewBox="0 0 24 24" class="w-6 h-6 fill-white" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="4"/><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 16a6 6 0 1 1 6-6 6 6 0 0 1-6 6z"/></svg>
                  </div>
                </div>
                <div class="px-3 pb-1 text-center">
                  <p class="text-white/60 text-[9px] font-bold uppercase tracking-widest leading-tight">Certifying</p>
                  <p class="text-white font-extrabold text-xs leading-tight mt-0.5">Drainlayer</p>
                </div>
                <div class="mx-3 mt-2 mb-3 py-1 rounded-lg text-center" style="background:rgba(255,255,255,0.15);">
                  <p class="text-white font-bold text-[10px]">Renewed</p>
                </div>
                <button class="mx-3 mb-3 py-1.5 rounded-lg text-center transition-all hover:opacity-90" style="background:rgba(255,255,255,0.2); color:#A7F3D0; font-size:10px; font-weight:700;">View Licence</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-1 flex items-center justify-center p-8">
        <div class="w-full max-w-md">
          <div class="flex items-center justify-center mb-8 lg:hidden">
            <img src="/logo.png" alt="NZ Plumbing" class="h-14 w-auto" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="hidden items-center gap-3">
              <div class="w-10 h-10 rounded-2xl flex items-center justify-center" style="background:linear-gradient(135deg,#2B9AD2,#5EC9F0);"><svg viewBox="0 0 24 24" class="w-5 h-5 fill-white" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C12 2 5 8.5 5 14a7 7 0 0014 0c0-5.5-7-12-7-12z"/></svg></div>
              <span class="font-bold text-slate-900 text-lg">NZ Plumbing</span>
            </div>
          </div>
          <div class="bg-white rounded-3xl p-8 shadow-sm" style="border:1px solid #EEF2F7;">
            <h1 class="text-2xl font-bold text-slate-900 mb-1">Welcome back</h1>
            <p class="text-slate-500 text-sm mb-8">Sign in to your admin account</p>
            @if (errorMsg()) {
              <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#FEF2F2; color:#EF4444; border:1px solid #FECACA;">
                <i class="ti ti-alert-circle text-[18px] shrink-0"></i>
                {{ errorMsg() }}
              </div>
            }
            <div class="space-y-5">
              <div>
                <label for="email" class="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
                <input id="email" type="text" [(ngModel)]="email" placeholder="admin@nzplumbing.co.nz" class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all" style="border-color:#E2E8F0; background:#FAFBFC;" onfocus="this.style.borderColor='#2B9AD2'; this.style.boxShadow='0 0 0 3px rgba(43,154,210,0.12)'" onblur="this.style.borderColor='#E2E8F0'; this.style.boxShadow='none'">
              </div>
              <div>
                <div class="flex items-center justify-between mb-1.5">
                  <label for="password" class="text-sm font-semibold text-slate-700">Password</label>
                  <a routerLink="/forgot-password" class="text-sm font-semibold hover:underline" style="color:#2B9AD2;">Forgot password?</a>
                </div>
                <input id="password" type="password" [(ngModel)]="password" (keyup.enter)="login()" placeholder="••••••••" class="w-full h-12 px-4 rounded-2xl text-sm border outline-none transition-all" style="border-color:#E2E8F0; background:#FAFBFC;" onfocus="this.style.borderColor='#2B9AD2'; this.style.boxShadow='0 0 0 3px rgba(43,154,210,0.12)'" onblur="this.style.borderColor='#E2E8F0'; this.style.boxShadow='none'">
              </div>
              <button (click)="login()" class="w-full h-12 text-white font-bold rounded-2xl shadow-sm transition-opacity hover:opacity-90 text-sm mt-2" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                {{ isLoading() ? 'Signing in...' : 'Sign In' }}
              </button>
            </div>
          </div>
          <p class="text-center text-xs text-slate-400 mt-6">© {{ year }} NZ Plumbing Limited. All rights reserved.</p>
        </div>
      </div>
    </div>
  `
})
export class LoginComponent {
  email = signal('');
  password = signal('');
  errorMsg = signal('');
  isLoading = signal(false);
  year = new Date().getFullYear();

  private router = inject(Router);
  private api = inject(ApiService);

  login() {
    if (this.isLoading()) return;
    if (!this.email().trim() || !this.password()) {
      this.errorMsg.set('Email and password are required.');
      return;
    }

    this.isLoading.set(true);
    this.api.login({ email: this.email(), password: this.password() }).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        this.errorMsg.set('');
        this.router.navigate(['/otp']);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.errorMsg.set(err.error?.message || 'Invalid email or password. Please try again.');
      }
    });
  }
}
